import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Dashboard.css';

// Import types
import { DashboardProps, SortField, SortDirection, TagSearchState, FilterState, Task } from '../types';

// Import hooks
import { useDashboardData } from '../hooks/useDashboardData';
import { useAI } from '../hooks/useAI';

// Import utilities
import { 
  getCustomPriority, 
  getPrioritySortValue, 
  getPriorityColorClass, 
  getDueDateClass,
  getCustomPriorityValues,
  formatProjectNames,
  getTaskSections,
  getTaskTags
} from '../utils/taskUtils';

// Import components
import Filters from './dashboard/Filters';
import AIInsights from './dashboard/AIInsights';
import CommentsView from './dashboard/CommentsView';

const Dashboard: React.FC<DashboardProps> = ({ accessToken, workspaceMembershipId, userId, user, onDisconnect }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Use custom hooks
  const dashboardData = useDashboardData(accessToken, userId, workspaceMembershipId);
  const ai = useAI();
  
  // Local state for UI interactions
  const [newTagName, setNewTagName] = useState('');
  const [tagSearchStates, setTagSearchStates] = useState<{[taskId: string]: TagSearchState}>({});
  const [tagToDelete, setTagToDelete] = useState<{id: string, name: string} | null>(null);
  
  // Determine active tab from URL
  const activeTab = location.pathname === '/comments' ? 'comments' : 'tasks';
  
  // Filter states
  const [projectFilter, setProjectFilter] = useState<string[]>([]);
  const [priorityFilter, setPriorityFilter] = useState<string[]>([]);
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [sectionFilter, setSectionFilter] = useState<string[]>([]);
  const [favoriteProjects, setFavoriteProjects] = useState<string[]>([]);
  
  // Sort states
  const [sortField, setSortField] = useState<SortField>('priority');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Load tags from localStorage
  useEffect(() => {
    const storedTags = localStorage.getItem('asana_tags');
    if (storedTags) {
      dashboardData.setTags(JSON.parse(storedTags));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save tags to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('asana_tags', JSON.stringify(dashboardData.tags));
  }, [dashboardData.tags]);

  // Load saved filters and favorites from localStorage
  useEffect(() => {
    const savedFilters = localStorage.getItem('dashboard_filters');
    if (savedFilters) {
      const filters = JSON.parse(savedFilters);
      setProjectFilter(filters.projectFilter || []);
      setPriorityFilter(filters.priorityFilter || []);
      setTagFilter(filters.tagFilter || []);
      setSectionFilter(filters.sectionFilter || []);
    }

    const savedFavorites = localStorage.getItem('favorite_projects');
    if (savedFavorites) {
      setFavoriteProjects(JSON.parse(savedFavorites));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    const filtersToSave = { projectFilter, priorityFilter, tagFilter, sectionFilter };
    localStorage.setItem('dashboard_filters', JSON.stringify(filtersToSave));
  }, [projectFilter, priorityFilter, tagFilter, sectionFilter]);

  // Fetch data on mount
  useEffect(() => {
    dashboardData.fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardData.fetchData]);

  // Fetch comments when switching to comments tab
  useEffect(() => {
    if (activeTab === 'comments' && !dashboardData.commentsSearchCompleted && !dashboardData.commentsLoading) {
      dashboardData.fetchCommentsData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dashboardData.commentsSearchCompleted, dashboardData.commentsLoading, dashboardData.fetchCommentsData]);

  // Helper function to get the tasks that should be shown
  const getTasksToShow = (): Task[] => {
    const tasksToShow = new Map<string, Task>();
    
    dashboardData.assignedTasks.forEach(task => {
      if (task.parent) {
        const parentTask = dashboardData.parentTasksWithSubtasks.find(parent => parent.name === task.parent?.name);
        if (parentTask) {
          tasksToShow.set(parentTask.gid, parentTask);
        }
      } else {
        tasksToShow.set(task.gid, task);
      }
    });
    
    return Array.from(tasksToShow.values());
  };

  // Get unique projects for filter dropdown
  const getUniqueProjects = () => {
    const projects = new Set<string>();
    const tasksToShow = getTasksToShow();
    
    tasksToShow.forEach(task => {
      if (task.projects && task.projects.length > 0) {
        task.projects.forEach(project => projects.add(project.name));
      }
    });
    
    return Array.from(projects).sort();
  };

  // Get sorted projects with favorites at top
  const getSortedProjects = () => {
    const allProjects = getUniqueProjects();
    const favoriteProjectList = allProjects.filter(project => favoriteProjects.includes(project));
    const otherProjects = allProjects.filter(project => !favoriteProjects.includes(project));
    return [...favoriteProjectList, ...otherProjects];
  };

  // Get project task counts
  const getProjectTaskCounts = () => {
    const projectCounts: { [key: string]: number } = {};
    const tasksToShow = getTasksToShow();

    tasksToShow.forEach(task => {
      if (task.projects && task.projects.length > 0) {
        task.projects.forEach(project => {
          projectCounts[project.name] = (projectCounts[project.name] || 0) + 1;
        });
      }
    });
    
    return projectCounts;
  };

  // Get section task counts
  const getSectionTaskCounts = () => {
    const sectionCounts: { [key: string]: number } = {};
    const tasksToShow = getTasksToShow();
    
    tasksToShow.forEach(task => {
      if (task.memberships) {
        task.memberships.forEach(membership => {
          if (membership.section && membership.section.name) {
            if (projectFilter.length > 0) {
              if (membership.project && projectFilter.includes(membership.project.name)) {
                sectionCounts[membership.section.name] = (sectionCounts[membership.section.name] || 0) + 1;
              }
            } else {
              sectionCounts[membership.section.name] = (sectionCounts[membership.section.name] || 0) + 1;
            }
          }
        });
      }
    });
    
    return sectionCounts;
  };

  // Get priority task counts
  const getPriorityTaskCounts = () => {
    const priorityCounts: { [key: string]: number } = {};
    const tasksToShow = getTasksToShow();
    
    tasksToShow.forEach(task => {
      const customPriority = getCustomPriority(task);
      if (customPriority) {
        priorityCounts[customPriority] = (priorityCounts[customPriority] || 0) + 1;
      }
    });
    
    return priorityCounts;
  };

  // Get tag task counts
  const getTagTaskCounts = () => {
    const tagCounts: { [key: string]: number } = {};
    const tasksToShow = getTasksToShow();
    
    tasksToShow.forEach(task => {
      const taskTags = getTaskTags(task.gid);
      taskTags.forEach(tag => {
        tagCounts[tag] = (tagCounts[tag] || 0) + 1;
      });
    });
    
    return tagCounts;
  };

  // Get unique sections for filter dropdown
  const getUniqueSections = () => {
    const sections = new Set<string>();
    const tasksToShow = getTasksToShow();
    
    tasksToShow.forEach(task => {
      if (task.memberships) {
        task.memberships.forEach(membership => {
          if (projectFilter.length > 0) {
            if (membership.project && projectFilter.includes(membership.project.name) && membership.section && membership.section.name) {
              sections.add(membership.section.name);
            }
          } else {
            if (membership.section && membership.section.name) {
              sections.add(membership.section.name);
            }
          }
        });
      }
    });
    
    return Array.from(sections).sort();
  };

  const getUniqueTags = () => {
    const allTags = new Set<string>();
    
    dashboardData.tags.forEach(tag => allTags.add(tag.name));
    
    const tasksToShow = getTasksToShow();
    tasksToShow.forEach(task => {
      const taskTags = getTaskTags(task.gid);
      taskTags.forEach(tag => allTags.add(tag));
    });
    
    return Array.from(allTags).sort();
  };

  // Filter and sort tasks
  const getFilteredAllTasks = () => {
    const allTasks = getTasksToShow().map(task => {
      const isParent = dashboardData.parentTasksWithSubtasks.some(parent => parent.gid === task.gid);
      return {
        ...task,
        tags: getTaskTags(task.gid),
        taskType: isParent ? 'parent' as const : 'assigned' as const,
        hasSubtasksAssigned: isParent,
        subtaskCount: isParent ? dashboardData.parentTasksWithSubtasks.find(parent => parent.gid === task.gid)?.subtasks?.length || 0 : undefined
      };
    });

    // Apply filters
    let filteredTasks = allTasks.filter(task => {
      // Filter by project
      if (projectFilter.length > 0) {
        if (!task.projects || task.projects.length === 0 || !task.projects.some(p => projectFilter.includes(p.name))) {
          return false;
        }
      }
      
      // Filter by priority
      if (priorityFilter.length > 0) {
        const customPriority = getCustomPriority(task);
        const hasMatchingPriority = priorityFilter.some(filterPriority => {
          if (customPriority && customPriority === filterPriority) return true;
          return false;
        });
        if (!hasMatchingPriority) {
          return false;
        }
      }
      
      // Filter by tag
      if (tagFilter.length > 0) {
        const taskTags = getTaskTags(task.gid);
        const hasMatchingTag = tagFilter.some(filterTag => taskTags.includes(filterTag));
        if (!hasMatchingTag) {
          return false;
        }
      }
      
      // Filter by section
      if (sectionFilter.length > 0) {
        const hasSection = task.memberships && task.memberships.some(membership => 
          membership.section && sectionFilter.includes(membership.section.name)
        );
        if (!hasSection) {
          return false;
        }
      }
      
      return true;
    });

    // Sort tasks
    filteredTasks.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortField) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'created_at':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        case 'due_on':
          aValue = a.due_on ? new Date(a.due_on).getTime() : Number.MAX_SAFE_INTEGER;
          bValue = b.due_on ? new Date(b.due_on).getTime() : Number.MAX_SAFE_INTEGER;
          break;
        case 'priority':
          const aCustomPriority = getCustomPriority(a);
          const bCustomPriority = getCustomPriority(b);
          const aSortValue = aCustomPriority ? getPrioritySortValue(aCustomPriority) : 999;
          const bSortValue = bCustomPriority ? getPrioritySortValue(bCustomPriority) : 999;
          aValue = aSortValue;
          bValue = bSortValue;
          break;
        case 'project':
          aValue = a.projects?.[0]?.name || '';
          bValue = b.projects?.[0]?.name || '';
          break;
        default:
          return 0;
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filteredTasks;
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  // Clear all filters
  const clearAllFilters = () => {
    setProjectFilter([]);
    setPriorityFilter([]);
    setTagFilter([]);
    setSectionFilter([]);
    localStorage.removeItem('dashboard_filters');
  };

  // Tag management functions
  const createTag = (e?: React.FormEvent | React.MouseEvent | React.KeyboardEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      e.nativeEvent?.preventDefault();
      e.nativeEvent?.stopImmediatePropagation();
    }
    
    if (newTagName.trim() && !dashboardData.tags.some(tag => tag.name === newTagName.trim())) {
      const newTag = {
        id: `tag-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: newTagName.trim()
      };
      dashboardData.setTags(prevTags => {
        // Double-check to avoid duplicates
        const existingTagNames = new Set(prevTags.map(tag => tag.name));
        if (existingTagNames.has(newTag.name)) {
          return prevTags; // Tag already exists, don't add
        }
        return [...prevTags, newTag];
      });
      setNewTagName('');
      
      // Keep focus on the input field
      setTimeout(() => {
        const input = document.querySelector('.create-tag input') as HTMLInputElement;
        if (input) {
          input.focus();
        }
      }, 0);
    }
    
    return false;
  };

  const createTagIfNotExists = (tagName: string) => {
    const existingTag = dashboardData.tags.find(tag => tag.name === tagName);
    if (existingTag) {
      return existingTag;
    }
    
    const newTag = {
      id: `tag-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: tagName
    };
    dashboardData.setTags(prevTags => {
      // Double-check to avoid duplicates
      const existingTagNames = new Set(prevTags.map(tag => tag.name));
      if (existingTagNames.has(tagName)) {
        return prevTags; // Tag already exists, don't add
      }
      return [...prevTags, newTag];
    });
    return newTag;
  };

  const addTagToTask = (taskId: string, tagName: string) => {
    createTagIfNotExists(tagName);
    dashboardData.addTagToTask(taskId, tagName);
    
    // Clear the search term after adding a tag
    setTagSearchStates(prev => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        searchTerm: ''
      }
    }));
  };

  const toggleTagDropdown = (taskId: string) => {
    setTagSearchStates(prev => ({
      ...prev,
      [taskId]: {
        isOpen: !prev[taskId]?.isOpen,
        searchTerm: prev[taskId]?.isOpen ? '' : (prev[taskId]?.searchTerm || '') // Clear search when closing
      }
    }));
  };

  const updateTagSearch = (taskId: string, searchTerm: string) => {
    setTagSearchStates(prev => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        searchTerm
      }
    }));
  };

  const getFilteredTagsForTask = (taskId: string) => {
    const taskTags = getTaskTags(taskId);
    const searchTerm = tagSearchStates[taskId]?.searchTerm?.toLowerCase() || '';
    
    return dashboardData.tags.filter(tag => 
      !taskTags.includes(tag.name) && 
      tag.name.toLowerCase().includes(searchTerm)
    );
  };

  // Close tag dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.tag-search-dropdown')) {
        setTagSearchStates(prev => {
          const newState = { ...prev };
          Object.keys(newState).forEach(taskId => {
            newState[taskId] = { ...newState[taskId], isOpen: false };
          });
          return newState;
        });
      }
    };

    // Prevent all form submissions globally
    const handleFormSubmit = (event: Event) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'FORM' || target.closest('form')) {
        const form = target.tagName === 'FORM' ? target as HTMLFormElement : target.closest('form');
        if (form && form.className.includes('create-tag')) {
          event.preventDefault();
          event.stopPropagation();
          return false;
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('submit', handleFormSubmit, true);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('submit', handleFormSubmit, true);
    };
  }, []);

  if (dashboardData.loading) {
    return <div className="dashboard-loading">Loading dashboard...</div>;
  }

  const filteredAllTasks = getFilteredAllTasks();
  const sortedProjects = getSortedProjects();
  const uniqueSections = getUniqueSections();
  const uniqueTags = getUniqueTags();
  const projectTaskCounts = getProjectTaskCounts();
  const sectionTaskCounts = getSectionTaskCounts();
  const priorityTaskCounts = getPriorityTaskCounts();
  const tagTaskCounts = getTagTaskCounts();
  const customPriorityValues = getCustomPriorityValues(getTasksToShow());

  const filters: FilterState = {
    projectFilter,
    priorityFilter,
    tagFilter,
    sectionFilter
  };

  return (
    <div 
      className="dashboard"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }}
    >
      <div className="dashboard-header">
        <div className="header-left">
          <h1>Dashboard</h1>
          <div className="menu-tabs">
            <button
              className={`menu-tab ${activeTab === 'tasks' ? 'active' : ''}`}
              onClick={() => navigate('/tasks')}
            >
              Tasks ({filteredAllTasks.length})
            </button>
            <button
              className={`menu-tab ${activeTab === 'comments' ? 'active' : ''}`}
              onClick={() => navigate('/comments')}
            >
              Pending Comments ({dashboardData.pendingComments.filter(c => !c.is_replied).length})
            </button>
          </div>
        </div>
        <div className="header-actions">
          <button 
            onClick={dashboardData.refreshData} 
            disabled={dashboardData.refreshing}
            className="refresh-button"
          >
            {dashboardData.refreshing ? 'Refreshing...' : 'Refresh Data'}
          </button>
          
          {user && (
            <div className="user-profile">
              <div className="user-info">
                <span className="user-name">{user.name}</span>
                <button onClick={onDisconnect} className="disconnect-button">
                  Disconnect
                </button>
              </div>
              {user.photo ? (
                <img 
                  src={user.photo.image_128x128} 
                  alt={user.name}
                  className="user-avatar"
                />
              ) : (
                <div className="user-avatar-placeholder">
                  {user.name.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="dashboard-content">
        {activeTab === 'tasks' && (
          <>
            <Filters
              filters={filters}
              setProjectFilter={setProjectFilter}
              setPriorityFilter={setPriorityFilter}
              setTagFilter={setTagFilter}
              setSectionFilter={setSectionFilter}
              favoriteProjects={favoriteProjects}
              setFavoriteProjects={setFavoriteProjects}
              sortedProjects={sortedProjects}
              uniqueSections={uniqueSections}
              uniqueTags={uniqueTags}
              customPriorityValues={customPriorityValues}
              projectTaskCounts={projectTaskCounts}
              sectionTaskCounts={sectionTaskCounts}
              priorityTaskCounts={priorityTaskCounts}
              tagTaskCounts={tagTaskCounts}
              onClearAllFilters={clearAllFilters}
            />

            <div className="main-content">
              <AIInsights
                openaiApiKey={ai.openaiApiKey}
                aiInsights={ai.aiInsights}
                aiLoading={ai.aiLoading}
                currentInsight={ai.currentInsight}
                usageStats={ai.usageStats}
                onAnalyze={() => ai.analyzeTasksWithAI(filteredAllTasks)}
                onCloseInsights={() => ai.clearInsights()}
                onShowApiKeyModal={() => ai.setShowApiKeyModal(true)}
                onClearAllData={() => ai.clearAllData()}
              />

              <div className="tasks-section">
                <h2>Tasks ({filteredAllTasks.length})</h2>
                
                <div className="tasks-table">
                  <table>
                    <thead>
                      <tr>
                        <th onClick={() => handleSort('name')} className="sortable">
                          Task Name {getSortIcon('name')}
                        </th>
                        <th>Type</th>
                        <th onClick={() => handleSort('project')} className="sortable">
                          Project {getSortIcon('project')}
                        </th>
                        <th>Section</th>
                        <th onClick={() => handleSort('created_at')} className="sortable">
                          Created {getSortIcon('created_at')}
                        </th>
                        <th onClick={() => handleSort('due_on')} className="sortable">
                          Due Date {getSortIcon('due_on')}
                        </th>
                        <th onClick={() => handleSort('priority')} className="sortable">
                          Priority {getSortIcon('priority')}
                        </th>
                        <th>Tags</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAllTasks.map((task) => {
                        const customPriority = getCustomPriority(task);
                        return (
                          <tr key={task.gid}>
                            <td>
                              <a 
                                href={`https://app.asana.com/0/0/${task.gid}/f`}
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="task-title-link"
                              >
                                {task.name}
                              </a>
                            </td>
                            <td>
                              {task.taskType === 'parent' ? (
                                <span className="task-type parent">
                                  Parent Task ({task.subtaskCount} subtask{task.subtaskCount !== 1 ? 's' : ''})
                                </span>
                              ) : (
                                <span className="task-type assigned">
                                  Task
                                </span>
                              )}
                            </td>
                            <td>{formatProjectNames(task.projects)}</td>
                            <td>{getTaskSections(task)}</td>
                            <td>{new Date(task.created_at).toLocaleDateString()}</td>
                            <td>
                              {task.due_on ? (
                                <span className={`due-date ${getDueDateClass(task.due_on)}`}>
                                  {new Date(task.due_on).toLocaleDateString()}
                                </span>
                              ) : (
                                <span className="due-date no-due-date">
                                  No Due Date
                                </span>
                              )}
                            </td>
                            <td>
                              {customPriority ? (
                                <span className={`priority ${getPriorityColorClass(customPriority)}`}>
                                  {customPriority}
                                </span>
                              ) : (
                                <span className="priority priority-none">
                                  No Priority
                                </span>
                              )}
                            </td>
                            <td 
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            >
                              <div className="task-tags">
                                {task.tags.map((tagName, index) => (
                                  <span key={index} className="tag">
                                    {tagName}
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        dashboardData.removeTagFromTask(task.gid, tagName);
                                      }}
                                      className="remove-tag"
                                      title={`Remove "${tagName}" tag`}
                                    >
                                      ×
                                    </button>
                                  </span>
                                ))}
                                
                                {/* Add tag dropdown */}
                                <div 
                                  className="tag-search-dropdown"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                >
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      toggleTagDropdown(task.gid);
                                    }}
                                    className="add-tag-button"
                                    title="Add tag to task"
                                  >
                                    + Add Tag
                                  </button>
                                  
                                  {tagSearchStates[task.gid]?.isOpen && (
                                    <div 
                                      className="tag-dropdown-menu"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                      }}
                                    >
                                      <input
                                        type="text"
                                        placeholder="Search or create tag..."
                                        value={tagSearchStates[task.gid]?.searchTerm || ''}
                                        onChange={(e) => updateTagSearch(task.gid, e.target.value)}
                                        onKeyPress={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                            const searchTerm = tagSearchStates[task.gid]?.searchTerm?.trim();
                                            if (searchTerm) {
                                              addTagToTask(task.gid, searchTerm);
                                              toggleTagDropdown(task.gid);
                                            }
                                          }
                                        }}
                                        className="tag-search-input"
                                        autoFocus
                                      />
                                      
                                      <div className="tag-options">
                                        {getFilteredTagsForTask(task.gid).map((tag) => (
                                          <div
                                            key={tag.id}
                                            className="tag-option"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              addTagToTask(task.gid, tag.name);
                                              toggleTagDropdown(task.gid);
                                            }}
                                          >
                                            {tag.name}
                                          </div>
                                        ))}
                                        
                                        {tagSearchStates[task.gid]?.searchTerm && 
                                         !dashboardData.tags.some(tag => 
                                           tag.name.toLowerCase() === tagSearchStates[task.gid]?.searchTerm?.toLowerCase()
                                         ) && (
                                          <div
                                            className="tag-option create-new"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              const searchTerm = tagSearchStates[task.gid]?.searchTerm || '';
                                              if (searchTerm.trim()) {
                                                addTagToTask(task.gid, searchTerm.trim());
                                                toggleTagDropdown(task.gid);
                                              }
                                            }}
                                          >
                                            Create "{tagSearchStates[task.gid]?.searchTerm}"
                                          </div>
                                        )}
                                        
                                        {getFilteredTagsForTask(task.gid).length === 0 && 
                                         !tagSearchStates[task.gid]?.searchTerm && (
                                          <div className="tag-option no-options">
                                            No tags available
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="tags-section">
                <h2>My Tags</h2>
                <div className="create-tag">
                  <input
                    type="text"
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    placeholder="New tag name"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        createTag(e);
                      }
                    }}
                  />
                  <button 
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      createTag(e);
                      return false;
                    }}
                  >
                    Create Tag
                  </button>
                </div>
                <div className="tags-list">
                  {dashboardData.tags.map((tag) => (
                    <span key={tag.id} className="tag">
                      {tag.name}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setTagToDelete({ id: tag.id, name: tag.name });
                        }}
                        className="remove-tag"
                        title={`Delete "${tag.name}" tag completely`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {dashboardData.tags.length === 0 && (
                    <p className="no-tags">No tags created yet. Create one above!</p>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'comments' && (
          <CommentsView
            pendingComments={dashboardData.pendingComments}
            commentsLoading={dashboardData.commentsLoading}
            commentsSearchCompleted={dashboardData.commentsSearchCompleted}
            commentsProgress={dashboardData.commentsProgress}
            onRefreshComments={dashboardData.fetchCommentsData}
          />
        )}
      </div>
      
      {/* Modals */}
      {tagToDelete && (
        <div 
          className="modal-overlay"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <div 
            className="modal"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <h3>Delete Tag</h3>
            <p>Are you sure you want to delete the tag "<strong>{tagToDelete.name}</strong>"?</p>
            <div className="modal-actions">
              <button 
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setTagToDelete(null);
                }} 
                className="cancel-button"
              >
                Cancel
              </button>
              <button 
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  if (tagToDelete) {
                    // Remove the tag from the tags list
                    dashboardData.setTags(prevTags => 
                      prevTags.filter(tag => tag.id !== tagToDelete.id)
                    );
                    
                    // Remove the tag from all tasks that have it
                    const tasksToUpdate = getTasksToShow();
                    tasksToUpdate.forEach(task => {
                      const taskTags = getTaskTags(task.gid);
                      if (taskTags.includes(tagToDelete.name)) {
                        dashboardData.removeTagFromTask(task.gid, tagToDelete.name);
                      }
                    });
                  }
                  setTagToDelete(null);
                }} 
                className="delete-button"
              >
                Delete Tag
              </button>
            </div>
          </div>
        </div>
      )}

      {ai.showApiKeyModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>🤖 Setup AI Analysis</h3>
            <p>Enter your OpenAI API key to enable AI-powered task insights:</p>
            <input
              type="password"
              placeholder="sk-..."
              value={ai.openaiApiKey}
              onChange={(e) => ai.setOpenaiApiKey(e.target.value)}
              className="api-key-input"
            />
            <p className="api-key-help">
              You can create an API key at{' '}
              <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">
                OpenAI Platform
              </a>
            </p>
            <div className="modal-actions">
              <button onClick={() => ai.setShowApiKeyModal(false)} className="cancel-button">
                Cancel
              </button>
              <button 
                onClick={() => ai.saveApiKey(ai.openaiApiKey)} 
                className="save-button"
                disabled={!ai.openaiApiKey.trim()}
              >
                Save & Analyze
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 